import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession } from '../services/api';

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);
  const navigate = useNavigate();

  // Stripe Price IDs - Hardcoded from Cloud Run configuration
  const MONTHLY_PRICE_ID = 'price_1SkJpP914aFkC6rOSnms6SQF';
  const YEARLY_PRICE_ID = 'price_1SkJqL914aFkC6rOJJeZBn08';

  const handleSubscribe = async (priceId, planName) => {
    if (!user || !user.id) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createCheckoutSession(priceId, user.id);
      
      if (response.data && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout session. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to start checkout process. Please try again.'
      );
      setLoading(false);
    }
  };

  const features = [
    'Unlimited travel plans',
    'AI-powered itinerary generation',
    'Advanced route optimization',
    'Priority customer support',
    'Export to PDF',
    'Share with friends',
  ];

  const plans = [
    {
      name: 'Monthly',
      price: '$7.99',
      period: 'per month + taxes',
      priceId: MONTHLY_PRICE_ID,
      description: 'Perfect for occasional travelers',
      popular: false,
    },
    {
      name: 'Yearly',
      price: '$59.99',
      period: 'per year + taxes',
      priceId: YEARLY_PRICE_ID,
      description: 'Best value - Save $35.89 per year',
      popular: true,
      savings: 'Save 37%',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Helmet>
        <title>Pricing Plans - Pelican Beaks</title>
        <meta name="description" content="Choose the perfect travel planning package. Affordable premium plans for unlimited AI itineraries." />
      </Helmet>
      
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ fontWeight: 700, mb: 2 }}
        >
          Choose Your Plan
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ maxWidth: '700px', mx: 'auto' }}
        >
          Unlock premium features and create unforgettable travel experiences
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={6} key={plan.name}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: plan.popular ? '2px solid' : '1px solid',
                borderColor: plan.popular ? 'primary.main' : 'divider',
                boxShadow: plan.popular ? 6 : 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 8,
                },
              }}
            >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {plan.savings}
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  {plan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {plan.description}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                    {plan.price}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" component="span">
                    {' '}{plan.period}
                  </Typography>
                </Box>

                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {features.map((feature, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1.5,
                      }}
                    >
                      <Check
                        size={20}
                        style={{
                          color: '#4caf50',
                          marginRight: '8px',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant={plan.popular ? 'contained' : 'outlined'}
                  size="large"
                  onClick={() => handleSubscribe(plan.priceId, plan.name)}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    `Subscribe to ${plan.name}`
                  )}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          All plans include a 14-day money-back guarantee. Cancel anytime.
        </Typography>
      </Box>
    </Container>
  );
};

export default Pricing;

